/**
 * Phase 77 — Season Report section registry.
 *
 * Single source of truth for every section that can appear in a Season
 * Report. The builder UI, the print template, the saved-snapshot shape,
 * and the smart-recommendation engine all consume this list, so adding a
 * new section is a one-place change (Part 7 — future compatibility).
 *
 * Each entry has:
 *  - key:        snake_case identifier used in `sections[key]` toggles
 *                and on the saved snapshot.
 *  - label:      human-readable label.
 *  - kind:       'data' = backed by a record count; 'narrative' = backed
 *                by a staff-typed text field.
 *  - count_key:  for kind === 'data', the key returned by
 *                GET /api/reports/season/{athlete_id}/counts.
 *  - text_field: for kind === 'narrative', the property on
 *                `coach_input` that holds the staff text. Several
 *                narrative sections reuse pre-existing coach_input fields
 *                (strengths / improvements / evaluation) for backward
 *                compatibility with already-saved reports.
 *  - default_on: initial checked state when the page first loads. Staff
 *                can override with Select All / Deselect All / Recommended.
 */
export const SEASON_REPORT_SECTIONS = [
  { key: 'executive_summary', label: 'Executive Summary',       kind: 'narrative', text_field: 'executive_summary', default_on: true  },
  { key: 'season_highlights', label: 'Season Highlights',       kind: 'data',      count_key: 'season_highlights',  default_on: true  },
  { key: 'attendance',        label: 'Attendance',              kind: 'data',      count_key: 'attendance',         default_on: true  },
  { key: 'match_reports',     label: 'Match Reports',           kind: 'data',      count_key: 'match_reports',      default_on: true  },
  { key: 'match_statistics',  label: 'Match Statistics',        kind: 'data',      count_key: 'match_statistics',   default_on: true  },
  { key: 'pdp',               label: 'PDP Review',              kind: 'data',      count_key: 'pdp',                default_on: true  },
  { key: 'fitness',           label: 'Fitness Assessments',     kind: 'data',      count_key: 'fitness',            default_on: false },
  { key: 'biometrics',        label: 'Biometrics',              kind: 'data',      count_key: 'biometrics',         default_on: false },
  { key: 'nutrition',         label: 'Nutrition',               kind: 'data',      count_key: 'nutrition',          default_on: false },
  { key: 'mental',            label: 'Mental Development',      kind: 'data',      count_key: 'mental',             default_on: false },
  { key: 'academic',          label: 'Academic',                kind: 'data',      count_key: 'academic',           default_on: false },
  { key: 'injury',            label: 'Injury & Recovery',       kind: 'data',      count_key: 'injury',             default_on: false },
  { key: 'career',            label: 'Career Pathway',          kind: 'data',      count_key: 'career',             default_on: false },
  { key: 'coach_feedback',    label: 'Coach Feedback',          kind: 'narrative', text_field: 'evaluation',        default_on: true  },
  { key: 'strengths',         label: 'Areas of Strength',       kind: 'narrative', text_field: 'strengths',         default_on: true  },
  { key: 'improvements',      label: 'Areas to Improve',        kind: 'narrative', text_field: 'improvements',      default_on: true  },
  { key: 'recommendations',   label: 'Recommendations',         kind: 'narrative', text_field: 'recommendations',   default_on: true  },
  { key: 'next_season_plan',  label: 'Next Season Action Plan', kind: 'narrative', text_field: 'next_season_plan',  default_on: true  },
];

// Convenience lookups -- O(1) section access by key.
export const SECTION_BY_KEY = Object.fromEntries(
  SEASON_REPORT_SECTIONS.map((s) => [s.key, s])
);

/**
 * Default section state (used on first page load and on Deselect-All-then-defaults).
 */
export function defaultSections() {
  const out = {};
  for (const s of SEASON_REPORT_SECTIONS) out[s.key] = s.default_on;
  return out;
}

/**
 * Compute a section's display status given the counts payload and the
 * narrative-text snapshot.
 *
 *   data section:      0  → 'none'  ("No Data Available")
 *                      1-2 → 'limited' ("Limited Data Available")
 *                      ≥3 → 'good' ("Good Data Available")
 *   narrative section: text present  → 'filled'  ("Filled")
 *                      text empty    → 'empty'   ("Empty")
 *
 * Returns { tone, label, count }. `count` is null for narrative sections.
 */
export function sectionStatus(section, ctx) {
  if (section.kind === 'narrative') {
    const txt = ctx?.narrativeText?.[section.text_field];
    const isFilled = !!stripHtml(txt || '').trim();
    return {
      tone: isFilled ? 'filled' : 'empty',
      label: isFilled ? 'Filled' : 'Empty',
      count: null,
    };
  }
  const c = Number(ctx?.counts?.[section.count_key] ?? 0);
  if (c === 0)   return { tone: 'none',    label: 'No Data Available',      count: 0 };
  if (c <= 2)    return { tone: 'limited', label: 'Limited Data Available', count: c };
  return         { tone: 'good',    label: 'Good Data Available',    count: c };
}

/**
 * Recommended preset — per Q3 confirmation:
 *   • Every narrative section is ON.
 *   • Every data section with count > 0 is ON.
 *   • Data sections with count === 0 are OFF.
 */
export function recommendedSections(ctx) {
  const out = {};
  for (const s of SEASON_REPORT_SECTIONS) {
    if (s.kind === 'narrative') {
      out[s.key] = true;
    } else {
      const c = Number(ctx?.counts?.[s.count_key] ?? 0);
      out[s.key] = c > 0;
    }
  }
  return out;
}

/** All sections ON. */
export function allOnSections() {
  const out = {};
  for (const s of SEASON_REPORT_SECTIONS) out[s.key] = true;
  return out;
}

/** All sections OFF. */
export function allOffSections() {
  const out = {};
  for (const s of SEASON_REPORT_SECTIONS) out[s.key] = false;
  return out;
}

/**
 * Backwards-compat: older saved reports used a 10-key sections dict
 * (training_attendance / match_reports / pdp + 7 modules). Merge any
 * missing keys onto the new registry so re-opening an old report
 * doesn't silently lose toggles.
 */
export function mergeSavedSections(saved) {
  const merged = defaultSections();
  if (!saved || typeof saved !== 'object') return merged;
  // Map legacy `training_attendance` → `attendance`.
  if ('training_attendance' in saved && !('attendance' in saved)) {
    merged.attendance = !!saved.training_attendance;
  }
  for (const s of SEASON_REPORT_SECTIONS) {
    if (s.key in saved) merged[s.key] = !!saved[s.key];
  }
  return merged;
}

function stripHtml(s) {
  return String(s || '').replace(/<[^>]*>/g, '');
}
